import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from "react";
import { Button, Modal, Row, Col } from "react-bootstrap";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const FONTS = [
  { name: "Dancing Script", label: "Elegant" },
  { name: "Caveat", label: "Casual" },
  { name: "Great Vibes", label: "Delicate" },
  { name: "Pacifico", label: "Bold Script" },
  { name: "Alex Brush", label: "Signature" },
];

const COLORS = ["#1a1a1a", "#1a4fd6", "#16a34a"];
const STROKES = [1.5, 3, 5];

const SignatureModal = ({ show, onClose, setCompanySignature, userData, mode = "admin" }) => {
  const { t } = useTranslation();
  const schema = yup.object({
    uploadedFiles: yup
      .array()
      .min(1, t("uploadSignatureErr"))
      .test(
        "fileSize",
        t("fileSizeErr"),
        (files) => files?.every(file => file.size <= 1.5 * 1024 * 1024)
      )
      .required(t("uploadSignatureErr"))
  });

  const {
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const [activeTab, setActiveTab] = useState("draw");
  const [saveDisabled, setSaveDisabled] = useState(true);

  // Draw
  const sigCanvasRef = useRef(null);
  const [penColor, setPenColor] = useState(COLORS[0]);
  const [penStroke, setPenStroke] = useState(STROKES[1]);

  // Type
  const typeCanvasRef = useRef(null);
  const [typedName, setTypedName] = useState("");
  const [selectedFont, setSelectedFont] = useState(FONTS[0].name);
  const [typeColor, setTypeColor] = useState(COLORS[0]);
  const [typedBase64, setTypedBase64] = useState(null);

  // Upload
  const fileInputRef = useRef(null);
  const [uploadedBase64, setUploadedBase64] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (!show) {
      setActiveTab("draw");
      setSaveDisabled(true);
      setPenColor(COLORS[0]);
      setPenStroke(STROKES[1]);
      setTypedName("");
      setSelectedFont(FONTS[0].name);
      setTypeColor(COLORS[0]);
      setTypedBase64(null);
      setUploadedBase64(null);
      setUploadedPreview(null);
      setDragging(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    setHasDrawn(false);
  }, [show]);

  const updateDrawState = () => {
    const isEmpty = sigCanvasRef.current?.isEmpty();
    setHasDrawn(!isEmpty);
    setSaveDisabled(isEmpty);
  };

  const MAX_SIZE = 1.5 * 1024 * 1024;
  const CANVAS_HEIGHT = 180;

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setUploadedPreview(preview);
    setValue("uploadedFiles", [file], { shouldValidate: true });

    if (file.size > MAX_SIZE) {
      setUploadedBase64(null);
      setSaveDisabled(true);
      return;
    }

    clearErrors("uploadedFiles");

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedBase64(
        e.target.result.replace(/^data:image\/[^;]+;base64,/, "")
      );
      setSaveDisabled(false);
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [".jpeg", ".png", ".jpg"] },
    multiple: false,
    onDrop
  });

  useLayoutEffect(() => {
    if (activeTab === "type" && show) {
      const canvas = typeCanvasRef.current;
      if (!canvas) return;

      const ratio = window.devicePixelRatio || 2;
      const width = canvas.offsetWidth;
      const height = CANVAS_HEIGHT;

      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      renderTyped(typedName, selectedFont, typeColor);
    }
  }, [activeTab, show]);

  // ── Draw ──────────────────────────────────────────────────────────────────
  const handleDrawEnd = () => {
    updateDrawState();
  };

  const handleUndo = () => {
    if (!sigCanvasRef.current) return;

    const data = sigCanvasRef.current.toData();
    if (data.length) {
      data.pop();
      sigCanvasRef.current.fromData(data);
    }

    updateDrawState();
  };

  const handleClearDraw = () => {
    sigCanvasRef.current?.clear();
    updateDrawState();
  };

  // ── Type ──────────────────────────────────────────────────────────────────
  const renderTyped = useCallback((name, font, color) => {
    const canvas = typeCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const ratio = window.devicePixelRatio || 2;
    const realWidth = canvas.width;
    const realHeight = canvas.height;
    const displayWidth = realWidth / ratio;
    const displayHeight = realHeight / ratio;

    ctx.clearRect(0, 0, realWidth, realHeight);

    if (!name.trim()) {
      setTypedBase64(null);
      setSaveDisabled(true);
      return;
    }

    let fontSize = displayHeight * 0.7;
    ctx.font = `${fontSize}px '${font}', cursive, serif`;

    while (ctx.measureText(name).width > displayWidth * 0.9 && fontSize > 30) {
      fontSize -= 2;
      ctx.font = `${fontSize}px '${font}', cursive, serif`;
    }

    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(name, displayWidth / 2, displayHeight / 2);

    const raw = canvas.toDataURL("image/png").replace(/^data:image\/png;base64,/, "");
    setTypedBase64(raw);
    setSaveDisabled(false);
  }, [selectedFont, typeColor]);

  useEffect(() => {
    if (activeTab === "type") {
      renderTyped(typedName, selectedFont, typeColor);
    }
  }, [selectedFont, typeColor]);

  const handleNameInput = (e) => { setTypedName(e.target.value); renderTyped(e.target.value, selectedFont, typeColor); };
  const handleFontChange = (f) => { setSelectedFont(f); renderTyped(typedName, f, typeColor); };
  const handleTypeColor = (c) => { setTypeColor(c); renderTyped(typedName, selectedFont, c); };
  const handleClearType = () => {
    setTypedName(""); setTypedBase64(null); setSaveDisabled(true);
    const canvas = typeCanvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  // ── Upload ────────────────────────────────────────────────────────────────
  const processImage = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedPreview(e.target.result);
      setUploadedBase64(e.target.result.replace(/^data:image\/[^;]+;base64,/, ""));
      setSaveDisabled(false);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processImage(e.dataTransfer.files[0]);
  };

  const handleClearUpload = () => {
    setUploadedBase64(null);
    setUploadedPreview(null);
    setSaveDisabled(true);
    setValue("uploadedFiles", null);
    clearErrors("uploadedFiles");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Tab switch ────────────────────────────────────────────────────────────
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSaveDisabled(true);

    if (tab !== "draw") sigCanvasRef.current?.clear(); if (tab !== "draw") {
      sigCanvasRef.current?.clear();
      setHasDrawn(false);
    }

    if (tab !== "type") {
      setTypedName("");
      setTypedBase64(null);
      const canvas = typeCanvasRef.current;
      if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    }

    if (tab !== "upload") {
      setUploadedBase64(null);
      setUploadedPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setValue("uploadedFiles", null);
      clearErrors("uploadedFiles");
    }
  };
  
  const trimCanvasToContent = (canvas, padding = 10) => {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const { data } = ctx.getImageData(0, 0, width, height);

    let minX = width, minY = height, maxX = -1, maxY = -1;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const isBackground = data[idx] > 250 && data[idx + 1] > 250 && data[idx + 2] > 250;
        if (!isBackground) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) return canvas;

    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(width - 1, maxX + padding);
    maxY = Math.min(height - 1, maxY + padding);

    const trimmedWidth = maxX - minX + 1;
    const trimmedHeight = maxY - minY + 1;

    const trimmedCanvas = document.createElement("canvas");
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    const trimmedCtx = trimmedCanvas.getContext("2d");
    trimmedCtx.fillStyle = "#ffffff";
    trimmedCtx.fillRect(0, 0, trimmedWidth, trimmedHeight);
    trimmedCtx.drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);

    return trimmedCanvas;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    let cleanBase64 = null;

    if (activeTab === "draw") {
      if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
        toast.error(t("provideSignatureErr") || "Please provide a signature.");
        return;
      }
      cleanBase64 = sigCanvasRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png")
        .replace(/^data:image\/png;base64,/, "");
    } else if (activeTab === "type") {
      if (!typedBase64) {
        toast.error(t("provideSignatureErr"), {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }

      const tempCanvas = document.createElement("canvas");
      const ctx = tempCanvas.getContext("2d");
      const WIDTH = 575;
      const HEIGHT = 117;

      tempCanvas.width = WIDTH;
      tempCanvas.height = HEIGHT;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, WIDTH, HEIGHT);

      const fontSize = 70;
      ctx.font = `${fontSize}px '${selectedFont}', cursive, serif`;
      const textWidth = ctx.measureText(typedName).width;

      let scaleX = 1;
      if (textWidth > WIDTH * 0.9) {
        scaleX = (WIDTH * 0.9) / textWidth;
        scaleX = Math.max(scaleX, 0.6);
      }

      ctx.setTransform(scaleX, 0, 0, 1, 0, 0);
      ctx.fillStyle = typeColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(typedName, (WIDTH / 2) / scaleX, HEIGHT / 2);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      cleanBase64 = trimCanvasToContent(tempCanvas)
        .toDataURL("image/png")
        .replace(/^data:image\/png;base64,/, "");
    } else if (activeTab === "upload") {
      if (!uploadedBase64) {
        setValue("uploadedFiles", null, { shouldValidate: true });
        await new Promise(resolve => setTimeout(resolve, 0));
        return;
      }
      cleanBase64 = uploadedBase64;
    }

    if (!cleanBase64) return;

    try {
      onClose();

      setCompanySignature(cleanBase64);
      if (mode === "admin") {
        try {
          const userId = localStorage.getItem("UserId");
          const payload = { ...userData, companySignature: cleanBase64, performedBy: userId, isSignatureUpdate: true, };

          const response = await axios.patch(
            `${process.env.REACT_APP_API_URL}/userRoute/updateUser/${userId}`,
            payload,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );

          if (response.status === 200) {
            toast.success(t("companySignatureUploadSuccessMsg"), {
              position: "top-right",
              autoClose: 5000,
            });
          }
        } catch (error) {
          toast.error(t("companySignatureUploadErrorMsg"), {
            position: "top-right",
            autoClose: 5000,
          });
        }
      }

    } catch (error) {
      console.error("Error updating company signature:", error);
    }
  };

  // ── Shared color picker ───────────────────────────────────────────────────
  const ColorPicker = ({ selected, onChange }) => (
    <div className="d-flex align-items-center gap-2">
      <span className="data-font-size text-muted">{t("color")}</span>
      {COLORS.map((c) => (
        <div
          key={c}
          onClick={() => onChange(c)}
          className={`sig-color-dot ${selected === c ? "sig-color-dot--active" : ""}`}
          style={{ background: c }}
        />
      ))}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal size="md" show={show} onHide={onClose} backdrop="static" centered>
      <Modal.Header closeButton className="py-2 px-3 border-bottom">
        <Modal.Title>{mode === "client" ? "Signature" : t("companySignature")}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-3">

        {/* ── Tab bar ──────────────────────────────────────────────────────── */}
        <div className="toggle-group d-inline-flex border-0 rounded-3 mb-3 w-100">
          {["draw", "type", "upload"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabSwitch(tab)}
              className={`border-0 flex-fill ${activeTab === tab ? "active" : ""}`}
            >
              {t(tab) || tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* ── DRAW TAB ─────────────────────────────────────────────────────── */}
        {activeTab === "draw" && (
          <>
            <p className="data-font-size text-muted mb-2">{t("useFingerToSign")}</p>

            <div className="sig-canvas-wrapper rounded mb-2 position-relative">
              <SignatureCanvas
                ref={sigCanvasRef}
                onEnd={handleDrawEnd}
                penColor={penColor}
                minWidth={penStroke * 0.5}
                maxWidth={penStroke}
                canvasProps={{ className: "sig-canvas d-block" }}
              />

              {hasDrawn && (
                <div className="position-absolute d-flex gap-1 sig-action-buttons">
                  <button
                    className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle p-0 sig-icon-btn"
                    onClick={handleUndo}
                    title={t("undo")}
                  >
                    <i className="fa fa-undo-alt"></i>
                  </button>
                  <button
                    className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle p-0 sig-icon-btn"
                    onClick={handleClearDraw}
                    title={t("clear")}
                  >
                    <i className="fa fa-broom"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap">
              <ColorPicker selected={penColor} onChange={setPenColor} />

              <div className="d-flex align-items-center gap-2">
                <span className="data-font-size text-muted">{t("stroke")}</span>
                {STROKES.map((sz) => (
                  <div
                    key={sz}
                    onClick={() => setPenStroke(sz)}
                    className={`sig-stroke-btn d-flex align-items-center justify-content-center rounded ${penStroke === sz ? "sig-stroke-btn--active" : "sig-stroke-btn--inactive"}`}
                  >
                    <div
                      className="rounded-circle sig-stroke-dot"
                      style={{ width: sz * 2.2, height: sz * 2.2 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── TYPE TAB ─────────────────────────────────────────────────────── */}
        {activeTab === "type" && (
          <>
            <p className="data-font-size text-muted mb-2">{t("chooseSignatureStyle")}</p>

            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Type your name..."
              value={typedName}
              onChange={handleNameInput}
              autoFocus
            />

            <div className="sig-canvas-wrapper rounded mb-2 position-relative">
              <canvas ref={typeCanvasRef} height={CANVAS_HEIGHT} className="sig-canvas d-block" />

              {typedName && (
                <div className="position-absolute sig-action-buttons">
                  <button
                    className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle p-0 sig-icon-btn"
                    onClick={handleClearType}
                    title={t("clear")}
                  >
                    <i className="fa fa-broom"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="mb-2">
              <span className="data-font-size text-muted d-block mb-1">{t("style")}</span>
              <Row className="g-2 mb-2">
                {FONTS.map((f) => (
                  <Col xs={2} key={f.name}>
                    <div
                      onClick={() => handleFontChange(f.name)}
                      className={`rounded-4 p-2 h-100 card-hover shadow-none cursor-pointer d-flex flex-column align-items-center justify-content-center sig-font-card ${selectedFont === f.name ? "card-success-soft sig-font-card--active" : "bg-white sig-font-card--inactive"}`}
                    >
                      <span
                        className="sig-font-preview"
                        style={{ fontFamily: `'${f.name}', cursive, serif`, color: typeColor }}
                      >
                        Aa
                      </span>
                      <span className="text-muted font-size-11 mt-1 text-center">{f.label}</span>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <div className="d-flex align-items-center gap-3">
              <ColorPicker selected={typeColor} onChange={handleTypeColor} />
            </div>
          </>
        )}

        {/* ── UPLOAD TAB ───────────────────────────────────────────────────── */}
        {activeTab === "upload" && (
          <>
            <p className="data-font-size text-muted mb-2">{t("uploadSignature")}</p>

            <div
              {...getRootProps()}
              className={`sig-canvas-wrapper rounded mb-2 position-relative d-flex align-items-center justify-content-center flex-column ${errors.uploadedFiles ? "border border-danger" : ""}`}
            >
              <input {...getInputProps()} />

              {uploadedPreview ? (
                <img
                  src={uploadedPreview}
                  alt="Uploaded signature"
                  className="sig-upload-preview"
                />
              ) : (
                <>
                  <img src={cloudUpload} alt="" className="sig-upload-icon mb-2" />
                  <span className="fw-semibold data-font-size text-muted">
                    {t("dragDropUpload")}
                  </span>
                  <span className="text-muted mt-1 font-size-11">{t("fileFormatInfo")}</span>
                </>
              )}

              {uploadedPreview && (
                <div className="position-absolute sig-action-buttons">
                  <button
                    className="btn btn-light shadow-sm d-flex align-items-center justify-content-center rounded-circle p-0 sig-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearUpload();
                    }}
                    title={t("clear")}
                  >
                    <i className="fa fa-broom"></i>
                  </button>
                </div>
              )}
            </div>

            {errors.uploadedFiles && (
              <p className="text-danger mt-2">{errors.uploadedFiles.message}</p>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="d-none"
              onChange={(e) => processImage(e.target.files[0])}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="py-2 px-3 border-top d-flex justify-content-end gap-2">
        <Button variant="falcon-default" size="sm" onClick={onClose}>
          {t("cancel") || "Cancel"}
        </Button>
        <Button variant="success" size="sm" onClick={handleSave} disabled={saveDisabled}>
          {t("save") || "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignatureModal;
